import {
  CheckCircleFilled,
  InfoCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { ReactComponent as SpinLoader } from '../../../../Assets/loader.svg';
import { Popover, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { AppContext } from '../../../../App';
import { logoBaseURL } from '../../../../Utils/constants';
import { pushEventToParent } from '../../../../Utils/eventUtils';
import { LOADERTYPE } from '../../../../Utils/types';
import { captureException } from '../../../../Utils/sentryUtils';
import { formatToINR } from '../../../../Utils/utils';
import { getImagePath } from '../../../../Utils/assetUtils';
import sibLogo from '../../../../Assets/sib/sib_logo.webp';
import { ROUTES } from '../../../../Utils/routes';

import styles from './index.module.css';

type Props = {
  bankDetails: any;
  isFetchingData?: boolean;
  isFailure?: boolean;
  fipId?: string;
  isConsentRevoked?: boolean;
  isOtherFiType?: boolean;
  isNameMismatch?: boolean;
  onCardClick?: () => void;
};

const AccountsCard: React.FC<Props> = ({
  bankDetails,
  isFetchingData,
  isFailure,
  fipId,
  isConsentRevoked,
  isOtherFiType = false,
  isNameMismatch,
  onCardClick,
}) => {
  const {
    setIsLoading,
    setSelectedFIP,
    setCurrentRoute,
    currentRoute,
    setPreviousRoute,
    setLoaderType,
    isAmountVisible,
    setFipSelectionPage,
    setPartialLoading,
    statusData,
  }: any = useContext(AppContext);

  const [isConsentRevoke, setIsConsentRevoke] = useState(false);
  const [isAccountsExpanded, setIsAccountsExpanded] = useState(false);

  useEffect(() => {
    let shouldRevoke = false;
    bankDetails.forEach((acc: any) => {
      const related = statusData.current.accountDetails.filter(
        (bank: any) => acc.linkedAccRef === bank.linkRefNumber,
      );
      const hasRevoke = related.some(
        (bank: any) => bank.status === 'REVOKE_INITIATED',
      );
      const hasActive = related.some((bank: any) => bank.status === 'ACTIVE');
      if (hasRevoke && !hasActive && !isConsentRevoked) {
        shouldRevoke = true;
      }
    });
    setIsConsentRevoke(shouldRevoke);
  }, [bankDetails, statusData.current.accountDetails]);

  const handleImageError = (event: any) => {
    event.target.src = getImagePath('images/bank.svg');
  };

  const totalBalance = useMemo(() => {
    return bankDetails
      .reduce((sum: number, acc: any) => sum + (acc.balance || 0), 0)
      .toString();
  }, [bankDetails]);

  // When a card mixes a failed account with a successfully fetched one,
  // the fetched-on date should reflect the successful account, not
  // whichever account happens to be first in the merged list.
  const latestFetchedAccount = useMemo(() => {
    return (
      bankDetails.find(
        (acc: any) => !acc.dataFailed && !acc.pending && acc.date,
      ) || bankDetails[0]
    );
  }, [bankDetails]);

  const handleConsentRecreation = (fipId: string) => {
    try {
      setLoaderType(LOADERTYPE.GENERIC);
      setPreviousRoute(currentRoute);
      setCurrentRoute(ROUTES.AAJOURNEY);
      setSelectedFIP([fipId]);
      setPartialLoading(true);
    } catch (error) {
      captureException(error);
      pushEventToParent({ eventCode: 'CONSENT_RECREATION_FAILED' });
    } finally {
      setIsLoading(false);
    }
  };

  const isYesBankFip =
    fipId ===
    (process.env.REACT_APP_IS_MOCK === 'true' ? 'YES-BANK' : 'YESB-FIP');

  const getAccountTypeLabel = (acc: any): string => {
    const fiType = (acc.fiType || '').toUpperCase();
    if (fiType === 'TERM_DEPOSIT' || fiType === 'TERM-DEPOSIT') {
      return 'Fixed Deposit';
    }
    if (fiType === 'RECURRING_DEPOSIT') {
      return 'Recurring Deposit';
    }
    if (fiType === 'DEPOSIT') {
      return 'Savings';
    }
    return '';
  };

  // Order the chips by fiType: Savings (DEPOSIT) first, then Fixed Deposit
  // (TERM_DEPOSIT), then Recurring Deposit (RECURRING_DEPOSIT).
  const sortedBankDetails = useMemo(() => {
    const fiTypeOrder: Record<string, number> = {
      DEPOSIT: 0,
      TERM_DEPOSIT: 1,
      'TERM-DEPOSIT': 1,
      RECURRING_DEPOSIT: 2,
    };
    return [...bankDetails].sort(
      (a: any, b: any) =>
        (fiTypeOrder[(a.fiType || '').toUpperCase()] ?? 99) -
        (fiTypeOrder[(b.fiType || '').toUpperCase()] ?? 99),
    );
  }, [bankDetails]);

  const renderSingleAccount = (acc: any, index: number) => {
    const label = getAccountTypeLabel(acc);
    const accNum = `xx ${(acc.maskedAccountNumber ?? acc.accNo)?.slice(-4)}`;
    return (
      <span
        key={index}
        className={styles.accountBadge}
      >
        {label ? `${label} · ${accNum}` : accNum}
      </span>
    );
  };

  const renderAccountNumbers = () => {
    if (sortedBankDetails.length <= 2) {
      return sortedBankDetails.map(renderSingleAccount);
    }

    if (isAccountsExpanded) {
      return (
        <>
          {sortedBankDetails.map(renderSingleAccount)}
          <span
            className={styles.showMoreText}
            onClick={(e) => {
              e.stopPropagation();
              setIsAccountsExpanded(false);
            }}
          >
            Show less
          </span>
        </>
      );
    }

    return (
      <>
        {renderSingleAccount(sortedBankDetails[0], 0)}
        <span
          className={styles.showMoreText}
          onClick={(e) => {
            e.stopPropagation();
            setIsAccountsExpanded(true);
          }}
        >
          +{sortedBankDetails.length - 1} accounts
        </span>
      </>
    );
  };

  // Check if account is successfully linked (not revoked, not failed, not pending)
  const isLinkedSuccessfully =
    !isConsentRevoked && !isFailure && !isFetchingData && !isNameMismatch;

  return (
    <div className={styles.cardContainer}>
      <div
        className={`${styles.card} ${isLinkedSuccessfully ? styles.cardWithBanner : ''}`}
        onClick={!isConsentRevoked && !isNameMismatch ? onCardClick : undefined}
      >
        {/* Bank Logo */}
        <div className={styles.logoContainer}>
          <img
            src={fipId === 'SIB' ? sibLogo : logoBaseURL + fipId + '.jpeg'}
            alt={bankDetails[0].fipName}
            className={styles.logo}
            onError={handleImageError}
          />
        </div>

        {/* Bank Info */}
        <div className={styles.bankInfo}>
          <Typography.Text className={styles.bankName}>
            {bankDetails[0].fipName}
          </Typography.Text>
          <div className={styles.accountNumbers}>
            {renderAccountNumbers()}
          </div>
        </div>

        {/* Right Section */}
        {isFailure ? (
          <div className={styles.failureSection}>
            <Popover
              content={
                <Typography.Text className={styles.popoverText}>
                  Unfortunately, we were not able to fetch your details from
                  bank. Please come back after some time.
                </Typography.Text>
              }
              trigger='click'
              overlayClassName={styles.popover}
            >
              <InfoCircleOutlined className={styles.infoIcon} />
            </Popover>
            <Typography.Text className={styles.failureText}>
              Data Fetch failed
            </Typography.Text>
          </div>
        ) : !isConsentRevoked ? (
          <div
            className={`${styles.balanceSection} ${isNameMismatch ? styles.balanceSectionNameMismatch : ''}`}
          >
            <div className={styles.balanceInfo}>
              {isConsentRevoke || isFetchingData ? (
                <Typography.Text className={styles.balance}>
                  {'₹X,XXX'}{' '}
                </Typography.Text>
              ) : (
                <Typography.Text className={styles.balance}>
                  {formatToINR(totalBalance, isAmountVisible)}
                </Typography.Text>
              )}
              {isConsentRevoke ? (
                <Typography.Text className={styles.statusText}>
                  revoking consent
                </Typography.Text>
              ) : isFetchingData ? (
                <Typography.Text className={styles.statusText}>
                  analyzing your data
                </Typography.Text>
              ) : latestFetchedAccount?.date ? (
                <Typography.Text className={styles.dateText}>
                  as on{' '}
                  {dayjs(latestFetchedAccount.date).format("DD MMM 'YY")}
                </Typography.Text>
              ) : null}
              {isNameMismatch && (
                <span className={styles.nameMismatchBadge}>Name Mismatch</span>
              )}
            </div>
            {isFetchingData ? (
              <SpinLoader className={styles.loadingIcon} />
            ) : (
              !isNameMismatch && <RightOutlined className={styles.arrowIcon} />
            )}
          </div>
        ) : (
          <div className={styles.connectSection}>
            <button
              className={styles.connectBtn}
              onClick={() => handleConsentRecreation(fipId!)}
            >
              Connect
            </button>
          </div>
        )}
      </div>

      {/* Success Info Text - shown when account is linked successfully */}
      {isLinkedSuccessfully && isYesBankFip && (
        <div className={styles.linkedInfoBanner}>
          <Typography.Text className={styles.linkedInfoText}>
            Your Yes Bank account has been already linked
          </Typography.Text>
        </div>
      )}

      {/* Consent Revoked Banner */}
      {isConsentRevoked && (
        <div className={styles.revokeBanner}>
          <img
            src={getImagePath('images/YBRevoke.svg')}
            alt='Consent Revoked'
            className={styles.revokeIcon}
          />
          <p className={styles.revokeText}>
            Consent revoked, restart tracking once again?
          </p>
        </div>
      )}
    </div>
  );
};

export default AccountsCard;
